import React, { useRef, useState } from 'react';

import { Toast } from './types';
import { useLegacyToastAdapter } from './ToastSagasAdapter';
import { ToastContext } from './ToastContext';

const defaultToastOptions: Partial<Toast> = {
  dismissAfterSeconds: 3,
};

const defaultErrorToastOptions: Partial<Toast> = {
  dismissAfterSeconds: 10,
};

const useToastInternals = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<{ [key in Toast['key']]: NodeJS.Timeout }>({});

  const clearToasts = () => {
    setToasts([]);
    Object.values(timers.current).forEach(value => {
      clearTimeout(value);
    });
    timers.current = {};
  };

  const clearToast = (key: Toast['key']) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.key !== key));
    const timer = timers.current[key];
    if (timer) {
      clearTimeout(timer);
      delete timers.current[key];
    }
  };

  const scheduleForRemoval = (toast: Toast) => {
    const timer = setTimeout(() => {
      clearToast(toast.key);
    }, toast.dismissAfterSeconds * 1000);
    timers.current = { ...timers.current, [toast.key]: timer };
  };

  const addToast = (toast: Toast) => {
    const toastWithDefaults =
      toast.type === 'error'
        ? { ...defaultErrorToastOptions, ...toast }
        : { ...defaultToastOptions, ...toast };
    setToasts(prevToasts => [toastWithDefaults, ...prevToasts]);
    if (toastWithDefaults.dismissAfterSeconds !== 0) {
      scheduleForRemoval(toastWithDefaults);
    }
  };

  return { toasts, addToast, clearToasts, clearToast };
};

export const ToastProvider = ({ children }) => {
  const toastInternals = useToastInternals();

  useLegacyToastAdapter({
    showToast: toastInternals.addToast,
    clearToasts: toastInternals.clearToasts,
  });

  return (
    <ToastContext.Provider value={toastInternals}>
      {children}
    </ToastContext.Provider>
  );
};
